<template>
  <div
    v-click-outside="clickOutsideConfig"
    class="notifications"
  >
    <button
      v-tooltip.bottom="{ content: 'Notifications', theme: 'info-tooltip' }"
      class="notifications__bell header__icon-link body-text body-text-large-400"
      :class="[{ clicked__button: opened }]"
      aria-label="Notifications"
      @click="toggle"
    >
      <icon
        class="img-icon-fill"
        data="@icon/bell.svg"
        :fill="true"
        width="var(--icon-24)"
        height="var(--icon-24)"
      />
      <DotNotification
        v-if="isHasNewNotification"
        class="notifications__indicator"
      />
    </button>

    <div
      v-if="(opened || importantOpened) && !disabledNotifications"
      class="notifications__box"
    >
      <BaseHeading
        level="3"
        color="monochrome-07"
        className="notifications__box_title"
      >
        Notifications
        <template v-if="isHasNewNotification"> ({{ newNotificationCount }}) </template>
      </BaseHeading>
      <div class="notifications__box_content">
        <div class="notifications__list custom-scroll">
          <template v-if="importantOpened ? isImportantNotificationExist : isNotificationExist">
            <Observer
              :items="importantOpened ? importantNotification : notifications"
              :last="last"
              elementClass="notifications__item"
              class="notifications__content"
              @intersect="scroll"
            >
              <template #default="{ item }">
                <component
                  :is="resolveNotificationComponent(item.type)"
                  :key="item.id"
                  :notification="item"
                  @demoVideoTimestampSelect="openDemoVideoTimestamp"
                />
              </template>
            </Observer>
          </template>
          <p v-else>No Notifications Found</p>
        </div>
        <div
          v-if="isLoadingNotification"
          class="notifications__loader"
        >
          <SpinnerDots :size="6" />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useRoute } from "vue-router"
import { directive as ClickOutside } from "click-outside-vue3"
import { computed, nextTick, ref, watch } from "vue"
import { useStore } from "vuex"

import DotNotification from "@/components/Dot/DotNotification.vue"
import BUG_WATERMARK_EXCEEDED from "@/components/Notification/NotificationByType/BUG_WATERMARK_EXCEEDED.vue"
import CALENDAR_EVENT from "@/components/Notification/NotificationByType/CALENDAR_EVENT.vue"
import COMMENTS from "@/components/Notification/NotificationByType/COMMENTS.vue"
import EPIC_COMMENTS from "@/components/Notification/NotificationByType/EPIC_COMMENTS.vue"
import EPIC_UPDATED from "@/components/Notification/NotificationByType/EPIC_UPDATED.vue"
import GOAL_REACHED from "@/components/Notification/NotificationByType/GOAL_REACHED.vue"
import HALT_SPRINT_NOTIFICATION from "@/components/Notification/NotificationByType/HALT_SPRINT_NOTIFICATION.vue"
import IDEA_ACTIVITY_CONVERTED_TO_EPIC from "@/components/Notification/NotificationByType/IDEA_ACTIVITY_CONVERTED_TO_EPIC.vue"
import IDEA_ACTIVITY_CONVERTED_TO_ITEM from "@/components/Notification/NotificationByType/IDEA_ACTIVITY_CONVERTED_TO_ITEM.vue"
import IDEA_ACTIVITY_CREATED from "@/components/Notification/NotificationByType/IDEA_ACTIVITY_CREATED.vue"
import IDEA_ACTIVITY_PROMOTED_TO_FEATURE from "@/components/Notification/NotificationByType/IDEA_ACTIVITY_PROMOTED_TO_FEATURE.vue"
import IDEA_ACTIVITY_UPDATE_DESCRIPTION from "@/components/Notification/NotificationByType/IDEA_ACTIVITY_UPDATE_DESCRIPTION.vue"
import IDEA_COMMENTS from "@/components/Notification/NotificationByType/IDEA_COMMENTS.vue"
import ITEM_CONVERTED_TO_EPIC from "@/components/Notification/NotificationByType/ITEM_CONVERTED_TO_EPIC.vue"
import ITEM_DEMO_VIDEO from "@/components/Notification/NotificationByType/ITEM_DEMO_VIDEO.vue"
import ITEM_UPDATE_ACTIVE_SPRINT_ADDED from "@/components/Notification/NotificationByType/ITEM_UPDATE_ACTIVE_SPRINT_ADDED.vue"
import ITEM_UPDATE_DESCRIPTION from "@/components/Notification/NotificationByType/ITEM_UPDATE_DESCRIPTION.vue"
import ITEM_UPDATE_NEXT_TASK_TO_WORK from "@/components/Notification/NotificationByType/ITEM_UPDATE_NEXT_TASK_TO_WORK.vue"
import ITEM_UPDATE_OWNER from "@/components/Notification/NotificationByType/ITEM_UPDATE_OWNER.vue"
import ITEM_UPDATE_SEVERITYLEVEL from "@/components/Notification/NotificationByType/ITEM_UPDATE_SEVERITYLEVEL.vue"
import ITEM_UPDATE_STATUS from "@/components/Notification/NotificationByType/ITEM_UPDATE_STATUS.vue"
import ITEM_UPDATE_STORYPOINTS from "@/components/Notification/NotificationByType/ITEM_UPDATE_STORYPOINTS.vue"
import ITEM_UPDATE_TASKS from "@/components/Notification/NotificationByType/ITEM_UPDATE_TASKS.vue"
import ITEM_UPDATE_TEAM from "@/components/Notification/NotificationByType/ITEM_UPDATE_TEAM.vue"
import ITEM_UPDATE_TYPE from "@/components/Notification/NotificationByType/ITEM_UPDATE_TYPE.vue"
import JIRA_BACKLOG_IMPORT_COMPLETED from "@/components/Notification/NotificationByType/JIRA_BACKLOG_IMPORT_COMPLETED.vue"
import JIRA_BACKLOG_IMPORT_DISABLED from "@/components/Notification/NotificationByType/JIRA_BACKLOG_IMPORT_DISABLED.vue"
import JIRA_SYNCHRONIZATION_DISABLED from "@/components/Notification/NotificationByType/JIRA_SYNCHRONIZATION_DISABLED.vue"
import MILESTONE_AT_RISK from "@/components/Notification/NotificationByType/MILESTONE_AT_RISK.vue"
import MILESTONE_BACK_TO_TRACK from "@/components/Notification/NotificationByType/MILESTONE_BACK_TO_TRACK.vue"
import MILESTONE_COMPLETED from "@/components/Notification/NotificationByType/MILESTONE_COMPLETED.vue"
import NEW_CURRENT_GOAL from "@/components/Notification/NotificationByType/NEW_CURRENT_GOAL.vue"
import PLATFORM_LOAD_METRICS_EXCEEDED from "@/components/Notification/NotificationByType/PLATFORM_LOAD_METRICS_EXCEEDED.vue"
import PROJECT_ADDITION from "@/components/Notification/NotificationByType/PROJECT_ADDITION.vue"
import REACTION_ADDED_TO_COMMENT from "@/components/Notification/NotificationByType/REACTION_ADDED_TO_COMMENT.vue"
import REPLY_COMMENT from "@/components/Notification/NotificationByType/REPLY_COMMENT.vue"
import SPRINT_RETROSPECTIVE from "@/components/Notification/NotificationByType/SPRINT_RETROSPECTIVE.vue"
import SPRINT_REVIEW_COMPLETED from "@/components/Notification/NotificationByType/SPRINT_REVIEW_COMPLETED.vue"
import SPRINT_RISK_ALERT from "@/components/Notification/NotificationByType/SPRINT_RISK_ALERT.vue"
import SPRINT_SYNC_ERROR from "@/components/Notification/NotificationByType/SPRINT_SYNC_ERROR.vue"
import STORY_POKER_CREATED from "@/components/Notification/NotificationByType/STORY_POKER_CREATED.vue"
import SUPPORT_CASE_CREATED from "@/components/Notification/NotificationByType/SUPPORT_CASE_CREATED.vue"
import TASK_OWNER_ASSIGMENT_NOTIFICATION from "@/components/Notification/NotificationByType/TASK_OWNER_ASSIGMENT_NOTIFICATION.vue"
import TASK_READINESS_NOTIFICATION from "@/components/Notification/NotificationByType/TASK_READINESS_NOTIFICATION.vue"
import TEAM_ADDITION from "@/components/Notification/NotificationByType/TEAM_ADDITION.vue"
import TIMESHEET_APPROVED from "@/components/Notification/NotificationByType/TIMESHEET_APPROVED.vue"
import TIMESHEET_DECLINED from "@/components/Notification/NotificationByType/TIMESHEET_DECLINED.vue"
import TODO_ASSIGNMENT from "@/components/Notification/NotificationByType/TODO_ASSIGNMENT.vue"
import TODO_CHANGE from "@/components/Notification/NotificationByType/TODO_CHANGE.vue"
import TODO_COMMENT from "@/components/Notification/NotificationByType/TODO_COMMENT.vue"
import TODO_REMINDER from "@/components/Notification/NotificationByType/TODO_REMINDER.vue"
import TODO_REVIEW_REQUEST from "@/components/Notification/NotificationByType/TODO_REVIEW_REQUEST.vue"
import TRIAL_EXPIRY_REMINDER from "@/components/Notification/NotificationByType/TRIAL_EXPIRY_REMINDER.vue"
import UNPRIORITIZED_BACKLOG_ADD_ITEM from "@/components/Notification/NotificationByType/UNPRIORITIZED_BACKLOG_ADD_ITEM.vue"
import UPCOMING_TIME_OFF_NOTIFICATION from "@/components/Notification/NotificationByType/UPCOMING_TIME_OFF_NOTIFICATION.vue"
import UPDATE_STORY_TIME_LEFT_REMINDER from "@/components/Notification/NotificationByType/UPDATE_STORY_TIME_LEFT_REMINDER.vue"
import UPDATE_TASK from "@/components/Notification/NotificationByType/UPDATE_TASK.vue"
import USER_MENTION from "@/components/Notification/NotificationByType/USER_MENTION.vue"
import WELCOME from "@/components/Notification/NotificationByType/WELCOME.vue"
import Observer from "@/components/Observer/index.vue"
import SpinnerDots from "@/components/SpinnerDots/index.vue"

import { ResourceEvent, ServerSendEventNotification } from "@/models"
import { FilterNotification } from "@/models/Filter"
import { Notification, NotificationType } from "@/models/Notification"
import { ServerResponse } from "@/models/ServerResponse"
import { emitterEmit } from "@/plugins/mitt-emitter"
import { Actions, Getters } from "@/store"
import { isFancyboxOpen } from "@/utils/fancyboxHelper"

import ITEM_DELETED from "./NotificationByType/ITEM_DELETED.vue"

import type { Component } from "vue"

const vClickOutside = ClickOutside
const store = useStore()
const route = useRoute()

const opened = ref(false)
const importantOpened = ref(false)
const isLoadingNotification = ref(false)
const filter = FilterNotification.createDefault()
const pageNumber = ref(1)

const notificationComponents: Record<string, Component> = {
  TODO_ASSIGNMENT,
  TODO_REMINDER,
  TODO_CHANGED: TODO_CHANGE,
  TODO_COMMENT_ACTIVITY: TODO_COMMENT,
  TODO_REVIEW_REQUEST,
  TODO_UPDATED: TODO_CHANGE,
  TODO_COMPLETED: TODO_CHANGE,
  TODO_COMMENT_ADDED: TODO_COMMENT,
  TODO_COMMENT_REPLY: TODO_COMMENT,
  TODO_COMMENT_MENTION: TODO_COMMENT,
  TODO_PM_REVIEW_REQUEST: TODO_REVIEW_REQUEST,
  TODO_CLIENT_REVIEW_REQUEST: TODO_REVIEW_REQUEST,
  SPRINT_SYNC_ERROR,
  UNPRIORITIZED_BACKLOG_ADD_ITEM,
  UPDATE_STORY_TIME_LEFT_REMINDER,
  SPRINT_RISK_ALERT,
  ITEM_UPDATE_STATUS,
  ITEM_UPDATE_STORYPOINTS,
  ITEM_UPDATE_SEVERITYLEVEL,
  ITEM_UPDATE_OWNER,
  ITEM_UPDATE_DESCRIPTION,
  ITEM_UPDATE_ACTIVE_SPRINT_ADDED,
  ITEM_DEMO_VIDEO,
  ITEM_UPDATE_TEAM,
  UPDATE_TASK,
  COMMENTS,
  BUG_WATERMARK_EXCEEDED,
  HALT_SPRINT_NOTIFICATION,
  SPRINT_REVIEW_COMPLETED,
  SUPPORT_CASE_CREATED,
  USER_MENTION,
  SPRINT_RETROSPECTIVE,
  STORY_POKER_CREATED,
  MILESTONE_AT_RISK,
  MILESTONE_BACK_TO_TRACK,
  MILESTONE_COMPLETED,
  GOAL_REACHED,
  WELCOME,
  PROJECT_ADDITION,
  TEAM_ADDITION,
  TASK_OWNER_ASSIGMENT_NOTIFICATION,
  TASK_READINESS_NOTIFICATION,
  JIRA_BACKLOG_IMPORT_COMPLETED,
  JIRA_BACKLOG_IMPORT_DISABLED,
  JIRA_SYNCHRONIZATION_DISABLED,
  TIMESHEET_APPROVED,
  TIMESHEET_DECLINED,
  ITEM_UPDATE_TASKS,
  ITEM_UPDATE_TYPE,
  ITEM_UPDATE_NEXT_TASK_TO_WORK,
  TRIAL_EXPIRY_REMINDER,
  ITEM_DELETED,
  ITEM_CONVERTED_TO_EPIC,
  REACTION_ADDED_TO_COMMENT,
  REPLY_COMMENT,
  EPIC_COMMENTS,
  IDEA_COMMENTS,
  EPIC_UPDATED,
  NEW_CURRENT_GOAL,
  IDEA_ACTIVITY_CREATED,
  IDEA_ACTIVITY_PROMOTED_TO_FEATURE,
  IDEA_ACTIVITY_CONVERTED_TO_EPIC,
  IDEA_ACTIVITY_CONVERTED_TO_ITEM,
  IDEA_ACTIVITY_UPDATE_DESCRIPTION,
  CALENDAR_EVENT_CREATED: CALENDAR_EVENT,
  CALENDAR_EVENT_UPDATED: CALENDAR_EVENT,
  CALENDAR_EVENT_DELETED: CALENDAR_EVENT,
  PLATFORM_LOAD_METRICS_EXCEEDED,
  UPCOMING_TIME_OFF_NOTIFICATION
}

const notifications = computed<Notification[]>(() => store.getters[Getters.GET_NOTIFICATIONS])
const disabledNotifications = computed<boolean>(() => store.getters[Getters.DISABLED_NOTIFICATIONS])
const serverResponse = computed<ServerResponse<Notification>>(
  () => store.getters[Getters.GET_NOTIFICATIONS_RESPONSE]
)
const newNotificationCount = computed<number>(() => store.getters[Getters.GET_NOTIFICATIONS_COUNT])
const serverSendUpdated = computed<(status: ResourceEvent) => ServerSendEventNotification>(
  () => store.getters[Getters.SUBSCRIPTION_MESSAGE_NOTIFICATION]
)
const notificationUpdatedMessage = computed(() => serverSendUpdated.value("NOTIFICATION_UPDATE"))

const importantNotification = computed(() =>
  notifications.value.filter(
    notification =>
      (!notification.read &&
        notification.type === NotificationType.JIRA_BACKLOG_IMPORT_COMPLETED) ||
      (!notification.read && notification.type === NotificationType.SPRINT_SYNC_ERROR)
  )
)
const importantNotificationsIds = computed(() =>
  importantNotification.value.map(notification => notification.id)
)
const isImportantNotificationExist = computed(() => importantNotification.value.length > 0)
const isNotificationExist = computed(() => notifications.value.length > 0)
const isHasNewNotification = computed(() => newNotificationCount.value > 0)
const last = computed(() => serverResponse.value?.last ?? true)
const clickOutsideConfig = computed(() => ({
  handler: hide,
  middleware: shouldHideOnOutsideClick
}))

watch(notificationUpdatedMessage, onListChanged)
watch(
  () => route.query.toDo,
  toDo => toDo && hide()
)

filter.addSort("createdDate", false)
void fetchNotifications(0)

function getNotifications(filterValue: FilterNotification): Promise<Notification[]> {
  return store.dispatch(Actions.GET_NOTIFICATIONS_RESPONSE, filterValue)
}

function readNotifications(): Promise<unknown> {
  return store.dispatch(Actions.READ_NOTIFICATIONS)
}

function setNotificationsCount(count: number): Promise<unknown[]> {
  return store.dispatch(Actions.SET_NOTIFICATIONS_COUNT, count)
}

function setNotificationsRead(notificationsIds: string[]): Promise<unknown[]> {
  return store.dispatch(Actions.SET_NOTIFICATION_READ, notificationsIds)
}

function resolveNotificationComponent(type: NotificationType) {
  return notificationComponents[type] ?? type
}

function onListChanged() {
  const { newNumberOfUnreadNotifications } = notificationUpdatedMessage.value
  if (newNumberOfUnreadNotifications) {
    void setNotificationsCount(newNumberOfUnreadNotifications)
  }

  if (opened.value || isHasNewNotification.value) {
    void fetchNotifications(0)
  }
}

function scroll() {
  // The sentinel keeps intersecting while a short page is on screen, so without a guard every
  // intersect starts another request and pages get skipped.
  if (isLoadingNotification.value || last.value) return

  pageNumber.value += 1
  void fetchNotifications(pageNumber.value)
}

async function fetchNotifications(page: number) {
  isLoadingNotification.value = true
  filter.page = page
  try {
    await getNotifications(filter)
  } finally {
    isLoadingNotification.value = false
  }

  if (isImportantNotificationExist.value && !opened.value) {
    importantOpened.value = true
  }
}

function toggle() {
  if (importantOpened.value) {
    importantOpened.value = false
    return
  }

  if (opened.value) {
    hide()
  } else {
    opened.value = true
  }
}

function hide() {
  if (!opened.value) return
  void setNotificationsCount(0)
  if (importantOpened.value) {
    void setNotificationsRead(importantNotificationsIds.value)
    importantOpened.value = false
  } else {
    void readNotifications()
    opened.value = false
  }
}

interface DemoVideoTimestampNotificationPayload {
  initialTimestampSeconds: number
  scrollToCommentId?: string
  itemUrl?: string
  demoVideoId?: string
}

async function closeNotificationsAfterModalOpen() {
  await nextTick()
  opened.value = false
  importantOpened.value = false
}

function openDemoVideoTimestamp(params: DemoVideoTimestampNotificationPayload) {
  if (!params.itemUrl) {
    emitterEmit("openDemoVideoTimestamp", {
      initialTimestampSeconds: params.initialTimestampSeconds,
      scrollToCommentId: params.scrollToCommentId,
      demoVideoId: params.demoVideoId
    })
    void closeNotificationsAfterModalOpen()
    return
  }

  const url = new URL(params.itemUrl, window.location.origin)
  const scrollToCommentId = params.scrollToCommentId || url.searchParams.get("comment") || undefined
  const modalParams = {
    initialTimestampSeconds: params.initialTimestampSeconds,
    scrollToCommentId,
    demoVideoId: params.demoVideoId
  }

  if (route.path === url.pathname) {
    emitterEmit("openDemoVideoTimestamp", modalParams)
    void closeNotificationsAfterModalOpen()
    return
  }

  url.searchParams.set("demoVideo", "true")
  url.searchParams.set("demoVideoTimestamp", String(params.initialTimestampSeconds))
  if (scrollToCommentId) {
    url.searchParams.set("comment", scrollToCommentId)
  }
  if (params.demoVideoId) {
    url.searchParams.set("demoVideoId", params.demoVideoId)
  }

  window.open(url.toString(), "_blank", "noopener,noreferrer")
}

function shouldHideOnOutsideClick(event: Event) {
  return !isIgnoredOutsideClick(event)
}

function isIgnoredOutsideClick(event: Event) {
  const target = event.target
  if (
    target instanceof Element &&
    target.closest(
      ".custom-modal.video, .plyr, .plyr__controls, .fancybox__container, .fancybox__content, .fancybox__backdrop"
    )
  ) {
    return true
  }

  return isVideoModalOpen() || isFancyboxOpen.value
}

function isVideoModalOpen() {
  const modals = document.querySelectorAll<HTMLElement>(".vm--modal.custom-modal.video")

  return Array.from(modals).some(modal => {
    const styles = window.getComputedStyle(modal)
    return styles.display !== "none" && styles.visibility !== "hidden"
  })
}
</script>
